import React, { useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { RotateCcw } from 'lucide-react';
import { logGovernedMcp } from '../governedMcpLogger';
import { useGovernedMcp } from '../governedMcpStore';
import EmptyState from '../components/EmptyState';
import { ghostBtn, selectField } from '../components/GatewayUi';
import { departmentMatches } from '../quotaDirectory';
import { buildSyntheticStatisticsUsers } from '../syntheticStatistics';
import type { QuotaUser } from '../governedMcpTypes';
import {
  aggregateDailySpend,
  aggregateModelSpend,
  compactTokens,
  clientLabel,
  currentQuotaMonth,
  formatQuotaMonth,
  formatTokens,
  remainingTokens,
  utilizationBreakdown,
  utilizationPercent,
} from '../quotaUsage';

function cardClass(isLight: boolean): string {
  return `rounded-2xl p-4 ${
    isLight
      ? 'bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] ring-1 ring-black/[0.04]'
      : 'bg-neutral-900 ring-1 ring-white/10'
  }`;
}

function dayLabel(isoDate: string): string {
  const parsed = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function ChartTooltip({
  active,
  payload,
  label,
  isLight,
}: {
  active?: boolean;
  payload?: { name: string; value: number; color?: string }[];
  label?: string;
  isLight: boolean;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className={`rounded-xl px-3 py-2 text-xs shadow-lg ${
        isLight ? 'bg-white ring-1 ring-black/10 text-neutral-700' : 'bg-neutral-800 ring-1 ring-white/10 text-neutral-200'
      }`}
    >
      {label ? <p className={`font-medium mb-1 ${isLight ? 'text-neutral-900' : 'text-white'}`}>{label}</p> : null}
      {payload.map((item) => (
        <p key={item.name} className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: item.color }} />
          {item.name}: <span className="tabular-nums font-medium">{formatTokens(item.value)}</span>
        </p>
      ))}
    </div>
  );
}

function FilterField({
  label,
  isLight,
  children,
}: {
  label: string;
  isLight: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="min-w-[148px] flex-1">
      <span className={`block text-[11px] font-medium mb-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{label}</span>
      {children}
    </label>
  );
}

function ChartCard({
  title,
  hint,
  isLight,
  children,
}: {
  title: string;
  hint: string;
  isLight: boolean;
  children: React.ReactNode;
}) {
  const heading = isLight ? 'text-neutral-900' : 'text-white';
  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  return (
    <div className={cardClass(isLight)}>
      <p className={`text-sm font-medium ${heading}`}>{title}</p>
      <p className={`text-xs mt-0.5 mb-3 ${muted}`}>{hint}</p>
      {children}
    </div>
  );
}

function groupSpend(rows: { name: string; tokens: number }[]): { name: string; tokens: number }[] {
  const byName = new Map<string, number>();
  for (const row of rows) byName.set(row.name, (byName.get(row.name) ?? 0) + row.tokens);
  return Array.from(byName.entries())
    .map(([name, tokens]) => ({ name, tokens }))
    .sort((left, right) => right.tokens - left.tokens)
    .slice(0, 8);
}

const MODEL_COLORS_LIGHT = ['#4f46e5', '#0ea5e9', '#7c3aed', '#059669', '#d97706'];
const MODEL_COLORS_DARK = ['#818cf8', '#38bdf8', '#a78bfa', '#34d399', '#fbbf24'];
const STATUS_COLORS = {
  ok: { light: '#059669', dark: '#34d399' },
  warning: { light: '#d97706', dark: '#fbbf24' },
  exceeded: { light: '#e11d48', dark: '#fb7185' },
};
const BAR = { light: '#4f46e5', dark: '#818cf8' };
const BAR_SOFT = { light: '#c7d2fe', dark: '#312e81' };

const EMPTY_FILTERS = {
  clientId: 'all',
  departmentId: 'all',
  roleId: 'all',
  userId: 'all',
};

export default function UsageStatisticsTab({ isLight }: { isLight: boolean }) {
  const { state, quotaDirectory } = useGovernedMcp();
  const [month, setMonth] = useState(currentQuotaMonth());
  const [clientId, setClientId] = useState(EMPTY_FILTERS.clientId);
  const [departmentId, setDepartmentId] = useState(EMPTY_FILTERS.departmentId);
  const [roleId, setRoleId] = useState(EMPTY_FILTERS.roleId);
  const [userId, setUserId] = useState(EMPTY_FILTERS.userId);

  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const title = isLight ? 'text-neutral-900' : 'text-white';
  const axis = isLight ? '#94a3b8' : '#737373';
  const grid = isLight ? '#f1f5f9' : '#27272a';
  const modelColors = isLight ? MODEL_COLORS_LIGHT : MODEL_COLORS_DARK;
  const bar = isLight ? BAR.light : BAR.dark;
  const barSoft = isLight ? BAR_SOFT.light : BAR_SOFT.dark;

  const universe = useMemo(
    () => buildSyntheticStatisticsUsers(month, state.clients, quotaDirectory),
    [month, quotaDirectory, state.clients]
  );

  const users = useMemo(() => {
    return universe.users.filter((user) => {
      if (clientId !== 'all' && user.clientId !== clientId) return false;
      if (userId !== 'all' && user.userId !== userId) return false;
      if (roleId !== 'all' && user.roleId !== roleId) return false;
      if (departmentId !== 'all') {
        const department = quotaDirectory.departments.find(
          (item) => item.id === departmentId || departmentMatches(item, departmentId)
        );
        const matches = department
          ? department.matchIds.includes(user.departmentId || '') || user.departmentId === department.id
          : user.departmentId === departmentId;
        if (!matches) return false;
      }
      return true;
    });
  }, [clientId, departmentId, quotaDirectory.departments, roleId, universe.users, userId]);

  const filtersDirty =
    clientId !== 'all' || departmentId !== 'all' || roleId !== 'all' || userId !== 'all' || month !== currentQuotaMonth();

  const resetFilters = () => {
    setMonth(currentQuotaMonth());
    setClientId(EMPTY_FILTERS.clientId);
    setDepartmentId(EMPTY_FILTERS.departmentId);
    setRoleId(EMPTY_FILTERS.roleId);
    setUserId(EMPTY_FILTERS.userId);
    logGovernedMcp('app', 'quota_statistics_reset');
  };

  const totals = useMemo(() => {
    return users.reduce(
      (acc, user) => {
        acc.limit += user.tokenLimit;
        acc.used += user.tokensUsed;
        acc.remaining += remainingTokens(user);
        if (user.tokensUsed >= user.tokenLimit) acc.over += 1;
        return acc;
      },
      { limit: 0, used: 0, remaining: 0, over: 0 }
    );
  }, [users]);

  const utilization = totals.limit > 0 ? Math.min(100, Math.round((totals.used / totals.limit) * 100)) : 0;
  const peopleCount = new Set(users.map((user) => user.userId || user.id)).size;

  const daily = useMemo(
    () =>
      aggregateDailySpend(users).map((point) => ({
        ...point,
        label: dayLabel(point.date),
      })),
    [users]
  );

  const byUser = useMemo(
    () =>
      users
        .reduce<QuotaUser[]>((acc, user) => {
          const existing = acc.find((item) => (item.userId || item.id) === (user.userId || user.id));
          if (!existing) {
            acc.push({ ...user });
            return acc;
          }
          existing.tokensUsed += user.tokensUsed;
          existing.tokenLimit += user.tokenLimit;
          return acc;
        }, [])
        .map((user) => ({
          id: user.userId || user.id,
          name: user.name.split(' ')[0] || user.name,
          fullName: user.name,
          role: user.role,
          tokens: user.tokensUsed,
          remaining: remainingTokens(user),
          percent: utilizationPercent(user),
        }))
        .sort((left, right) => right.tokens - left.tokens),
    [users]
  );

  const byDepartment = useMemo(
    () =>
      groupSpend(
        users.map((user) => ({
          name:
            universe.departments.find((item) => item.id === user.departmentId)?.name ||
            quotaDirectory.departments.find((item) => item.id === user.departmentId)?.name ||
            'Unassigned',
          tokens: user.tokensUsed,
        }))
      ),
    [quotaDirectory.departments, universe.departments, users]
  );

  const byRole = useMemo(
    () =>
      groupSpend(
        users.map((user) => ({
          name: user.role || universe.roles.find((item) => item.id === user.roleId)?.name || 'Unassigned',
          tokens: user.tokensUsed,
        }))
      ),
    [universe.roles, users]
  );

  const byClient = useMemo(
    () =>
      groupSpend(
        users.map((user) => ({
          name: clientLabel(user.clientId, universe.clients),
          tokens: user.tokensUsed,
        }))
      ),
    [universe.clients, users]
  );

  const byModel = useMemo(() => aggregateModelSpend(users), [users]);
  const mix = useMemo(() => utilizationBreakdown(users), [users]);
  const modelTotal = byModel.reduce((sum, row) => sum + row.tokens, 0);
  const usedVsRemain = byUser.slice(0, 6).map((row) => ({
    name: row.name,
    Used: row.tokens,
    Remaining: row.remaining,
  }));

  const selectClass = `${selectField(isLight)} w-full`;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className={`text-sm font-medium ${title}`}>Usage statistics</p>
          <p className={`text-xs mt-0.5 ${muted}`}>
            {formatQuotaMonth(month)} · sample spend to preview filters and trends
          </p>
        </div>
        <button type="button" className={ghostBtn(isLight)} onClick={resetFilters} disabled={!filtersDirty}>
          <RotateCcw size={13} />
          Reset
        </button>
      </div>

      <div className={`${cardClass(isLight)} !p-3`}>
        <div className="flex flex-wrap items-end gap-2.5">
          <FilterField label="Month" isLight={isLight}>
            <input
              type="month"
              className={selectClass}
              value={month}
              onChange={(event) => {
                logGovernedMcp('app', 'quota_statistics_month', { month: event.target.value });
                setMonth(event.target.value);
              }}
            />
          </FilterField>
          <FilterField label="Client" isLight={isLight}>
            <select
              className={selectClass}
              value={clientId}
              onChange={(event) => {
                logGovernedMcp('app', 'quota_statistics_client_filter', { clientId: event.target.value });
                setClientId(event.target.value);
              }}
            >
              <option value="all">All clients</option>
              {universe.clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </FilterField>
          <FilterField label="Department" isLight={isLight}>
            <select
              className={selectClass}
              value={departmentId}
              onChange={(event) => {
                logGovernedMcp('app', 'quota_statistics_department_filter', { departmentId: event.target.value });
                setDepartmentId(event.target.value);
              }}
            >
              <option value="all">All departments</option>
              {universe.departments.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </FilterField>
          <FilterField label="Role" isLight={isLight}>
            <select
              className={selectClass}
              value={roleId}
              onChange={(event) => {
                logGovernedMcp('app', 'quota_statistics_role_filter', { roleId: event.target.value });
                setRoleId(event.target.value);
              }}
            >
              <option value="all">All roles</option>
              {universe.roles.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </FilterField>
          <FilterField label="User" isLight={isLight}>
            <select
              className={selectClass}
              value={userId}
              onChange={(event) => {
                logGovernedMcp('app', 'quota_statistics_user_filter', { userId: event.target.value });
                setUserId(event.target.value);
              }}
            >
              <option value="all">All users</option>
              {universe.people.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </FilterField>
        </div>
      </div>

      {users.length === 0 ? (
        <EmptyState
          isLight={isLight}
          title="No spending data"
          description="Nothing matches these filters. Reset or choose another client, department, role, user, or month."
        />
      ) : (
        <>
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
            {[
              { label: 'Current spend', value: compactTokens(totals.used), hint: `${formatTokens(totals.used)} tokens` },
              { label: 'Remaining', value: compactTokens(totals.remaining), hint: `of ${compactTokens(totals.limit)} assigned` },
              { label: 'Utilization', value: `${utilization}%`, hint: `${totals.over} over limit` },
              { label: 'People', value: String(peopleCount), hint: `${users.length} client assignments` },
            ].map((item) => (
              <div key={item.label} className={cardClass(isLight)}>
                <p className={`text-[11px] uppercase tracking-wide ${muted}`}>{item.label}</p>
                <p className={`mt-1 text-xl font-semibold tabular-nums tracking-tight ${title}`}>{item.value}</p>
                <p className={`mt-0.5 text-xs ${muted}`}>{item.hint}</p>
              </div>
            ))}
          </div>

          <ChartCard title="Daily spend" hint={`${formatQuotaMonth(month)} · token usage across the selected scope`} isLight={isLight}>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={daily} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="usage-spend-fill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={bar} stopOpacity={isLight ? 0.22 : 0.35} />
                      <stop offset="100%" stopColor={bar} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} minTickGap={18} />
                  <YAxis
                    tick={{ fill: axis, fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    width={46}
                    tickFormatter={(value: number) => compactTokens(value)}
                  />
                  <Tooltip content={<ChartTooltip isLight={isLight} />} />
                  <Area type="monotone" dataKey="tokens" name="Tokens" stroke={bar} fill="url(#usage-spend-fill)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          <div className="grid lg:grid-cols-2 gap-4">
            <ChartCard title="Top spenders" hint="Highest token use in this filter" isLight={isLight}>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byUser.slice(0, 8)} layout="vertical" margin={{ top: 4, right: 12, left: 4, bottom: 0 }}>
                    <CartesianGrid stroke={grid} strokeDasharray="3 3" horizontal={false} />
                    <XAxis
                      type="number"
                      tick={{ fill: axis, fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(value: number) => compactTokens(value)}
                    />
                    <YAxis type="category" dataKey="name" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} width={64} />
                    <Tooltip
                      content={<ChartTooltip isLight={isLight} />}
                      labelFormatter={(_, payload) => {
                        const row = payload?.[0]?.payload as { fullName?: string; role?: string } | undefined;
                        return row?.fullName ? `${row.fullName}${row.role ? ` · ${row.role}` : ''}` : '';
                      }}
                    />
                    <Bar dataKey="tokens" name="Tokens" radius={[0, 6, 6, 0]} barSize={14} fill={bar} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            <ChartCard title="Used vs remaining" hint="Capacity left for the top people" isLight={isLight}>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={usedVsRemain} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis
                      tick={{ fill: axis, fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      width={42}
                      tickFormatter={(value: number) => compactTokens(value)}
                    />
                    <Tooltip content={<ChartTooltip isLight={isLight} />} />
                    <Bar dataKey="Used" stackId="cap" fill={bar} radius={[0, 0, 0, 0]} barSize={22} />
                    <Bar dataKey="Remaining" stackId="cap" fill={barSoft} radius={[6, 6, 0, 0]} barSize={22} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <ChartCard title="Spend by department" hint="Where tokens are concentrated" isLight={isLight}>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byDepartment} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fill: axis, fontSize: 10 }} axisLine={false} tickLine={false} interval={0} />
                    <YAxis
                      tick={{ fill: axis, fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      width={42}
                      tickFormatter={(value: number) => compactTokens(value)}
                    />
                    <Tooltip content={<ChartTooltip isLight={isLight} />} />
                    <Bar dataKey="tokens" name="Tokens" radius={[6, 6, 0, 0]} barSize={28} fill={bar} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            <ChartCard title="Spend by role" hint="Usage grouped by assigned role" isLight={isLight}>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byRole} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fill: axis, fontSize: 10 }} axisLine={false} tickLine={false} interval={0} />
                    <YAxis
                      tick={{ fill: axis, fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      width={42}
                      tickFormatter={(value: number) => compactTokens(value)}
                    />
                    <Tooltip content={<ChartTooltip isLight={isLight} />} />
                    <Bar dataKey="tokens" name="Tokens" radius={[6, 6, 0, 0]} barSize={28} fill={isLight ? '#0ea5e9' : '#38bdf8'} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <ChartCard title="Spend by model" hint="Share of current token spend" isLight={isLight}>
              <div className="flex items-center gap-4">
                <div className="h-52 w-52 shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={byModel} dataKey="tokens" nameKey="model" innerRadius={52} outerRadius={78} paddingAngle={2} stroke="none">
                        {byModel.map((row, index) => (
                          <Cell key={row.model} fill={modelColors[index % modelColors.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<ChartTooltip isLight={isLight} />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <ul className="flex-1 min-w-0 space-y-2">
                  {byModel.map((row, index) => {
                    const share = modelTotal > 0 ? Math.round((row.tokens / modelTotal) * 100) : 0;
                    return (
                      <li key={row.model} className="flex items-center justify-between gap-3 text-sm">
                        <span className="flex items-center gap-2 min-w-0">
                          <span className="h-2 w-2 rounded-full shrink-0" style={{ background: modelColors[index % modelColors.length] }} />
                          <span className={`truncate ${isLight ? 'text-neutral-700' : 'text-neutral-200'}`}>{row.model}</span>
                        </span>
                        <span className={`tabular-nums shrink-0 ${muted}`}>
                          {compactTokens(row.tokens)} · {share}%
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </ChartCard>

            <ChartCard title="Spend by client" hint="Token spend per AI client" isLight={isLight}>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byClient} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fill: axis, fontSize: 10 }} axisLine={false} tickLine={false} interval={0} />
                    <YAxis
                      tick={{ fill: axis, fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      width={42}
                      tickFormatter={(value: number) => compactTokens(value)}
                    />
                    <Tooltip content={<ChartTooltip isLight={isLight} />} />
                    <Bar dataKey="tokens" name="Tokens" radius={[6, 6, 0, 0]} barSize={32} fill={isLight ? '#7c3aed' : '#a78bfa'} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <ChartCard title="Utilization mix" hint="People by quota health" isLight={isLight}>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={mix} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} width={28} />
                    <Tooltip content={<ChartTooltip isLight={isLight} />} />
                    <Bar dataKey="value" name="Users" radius={[6, 6, 0, 0]} barSize={36}>
                      {mix.map((row) => (
                        <Cell key={row.key} fill={isLight ? STATUS_COLORS[row.key].light : STATUS_COLORS[row.key].dark} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            <ChartCard title="Highest spend" hint="Used versus assigned capacity" isLight={isLight}>
              <ul className="space-y-2.5">
                {byUser.slice(0, 5).map((row) => (
                  <li key={row.id}>
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <div className="min-w-0">
                        <p className={`text-sm font-medium truncate ${title}`}>{row.fullName}</p>
                        <p className={`text-[11px] truncate ${muted}`}>{row.role || '—'}</p>
                      </div>
                      <p className={`text-xs tabular-nums shrink-0 ${muted}`}>
                        {compactTokens(row.tokens)} · {row.percent}%
                      </p>
                    </div>
                    <div className={`h-1.5 rounded-full overflow-hidden ${isLight ? 'bg-neutral-100' : 'bg-neutral-800'}`}>
                      <div
                        className={`h-full rounded-full ${
                          row.percent >= 100 ? 'bg-rose-500' : row.percent >= 80 ? 'bg-amber-500' : isLight ? 'bg-indigo-500' : 'bg-indigo-400'
                        }`}
                        style={{ width: `${Math.min(100, row.percent)}%` }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </ChartCard>
          </div>
        </>
      )}
    </div>
  );
}
